<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[9999] overflow-y-auto"
      aria-labelledby="installation-modal-title"
      role="dialog"
      aria-modal="true"
      @click.self="$emit('close')"
    >
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-[9998]"
          aria-hidden="true"
          @click="$emit('close')"
        ></div>

        <!-- Center modal -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div class="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full relative z-[10000]">
          <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 sm:mx-0 sm:h-10 sm:w-10">
                <svg class="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"></path>
                </svg>
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="installation-modal-title">
                  Installera {{ agent?.name }}
                </h3>
                <div class="mt-4 space-y-4">
                  <!-- Step 1: Domain Check -->
                  <div class="rounded-md bg-yellow-50 dark:bg-yellow-900/20 p-3 border border-yellow-200 dark:border-yellow-800">
                    <div class="flex">
                      <div class="flex-shrink-0">
                        <svg class="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                        </svg>
                      </div>
                      <div class="ml-3">
                        <h4 class="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                          Steg 1: Godkänn din domän FÖRST
                        </h4>
                        <p class="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                          <strong>VIKTIGT:</strong> Lägg till din domän (t.ex. <code class="bg-yellow-100 dark:bg-yellow-900 px-1 rounded">taxijakt.se</code>) i <strong>Approved Domains</strong> innan du installerar widgeten. Agenten fungerar <strong>ENDAST</strong> på din egen domän, INTE på ai-agent-frontend-a8x.pages.dev.
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Step 2: Copy Code -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Steg 2: Kopiera chat widget-koden
                    </label>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      Denna kod skapar en flytande chat-ikon i nedre högra hörnet (max 380px bred)
                    </p>
                    <div class="flex items-start space-x-2">
                      <textarea
                        :value="embedCode"
                        readonly
                        rows="8"
                        class="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-xs font-mono"
                      ></textarea>
                      <button
                        @click="copyCode"
                        class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                        title="Kopiera kod"
                      >
                        <svg v-if="copied" class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                        </svg>
                      </button>
                    </div>
                    <p v-if="copied" class="mt-1 text-xs text-green-600 dark:text-green-400">
                      ✓ Kod kopierad!
                    </p>
                  </div>

                  <!-- Step 3: Where to Place -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Steg 3: Var ska koden placeras?
                    </label>
                    <div class="bg-gray-50 dark:bg-gray-700/50 rounded-md p-4 space-y-3">
                      <div class="flex items-start">
                        <div class="flex-shrink-0">
                          <span class="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-xs font-semibold">1</span>
                        </div>
                        <div class="ml-3">
                          <p class="text-sm text-gray-700 dark:text-gray-300">
                            <strong>På din webbplats:</strong> Lägg till koden precis före stängande <code class="bg-gray-200 dark:bg-gray-600 px-1 rounded">&lt;/body&gt;</code>-taggen på <code class="bg-gray-200 dark:bg-gray-600 px-1 rounded">taxijakt.se</code>. Chatten kommer att visas som en flytande ikon i nedre högra hörnet.
                          </p>
                        </div>
                      </div>
                      <div class="flex items-start">
                        <div class="flex-shrink-0">
                          <span class="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-xs font-semibold">2</span>
                        </div>
                        <div class="ml-3">
                          <p class="text-sm text-gray-700 dark:text-gray-300">
                            <strong>Automatisk:</strong> Chat-widgeten laddas automatiskt och användare kan klicka på ikonen för att öppna chatten. Chatten är 380px bred (max 340px på mobil).
                          </p>
                        </div>
                      </div>
                      <div class="flex items-start">
                        <div class="flex-shrink-0">
                          <span class="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-xs font-semibold">3</span>
                        </div>
                        <div class="ml-3">
                          <p class="text-sm text-gray-700 dark:text-gray-300">
                            <strong>Obs:</strong> Testa ALLTID på din egen domän (t.ex. taxijakt.se). Agenten fungerar INTE om du öppnar URL:en direkt i webbläsaren.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Example Code -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Exempel: Komplett HTML-sida
                    </label>
                    <div class="flex items-start space-x-2">
                      <textarea
                        :value="fullPageExample"
                        readonly
                        rows="15"
                        class="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-xs font-mono"
                      ></textarea>
                      <button
                        @click="copyFullExample"
                        class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                        title="Kopiera komplett exempel"
                      >
                        <svg v-if="copiedFull" class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <!-- Success Message -->
                  <div class="rounded-md bg-green-50 dark:bg-green-900/20 p-3 border border-green-200 dark:border-green-800">
                    <p class="text-sm text-green-800 dark:text-green-200">
                      <strong>Klart!</strong> När du har lagt till koden på din webbplats kommer agenten att fungera automatiskt på din godkända domän.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              @click="$emit('close')"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Stäng
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

interface Agent {
  id: number | string
  agent_id: string
  name: string
  color_theme?: string
  agent_names?: string[] | string | null
}

interface Props {
  isOpen: boolean
  agent: Agent | null
  agentUrl: string
}

const props = defineProps<Props>()
defineEmits<{
  (e: 'close'): void
}>()

// State for copy feedback
const copied = ref(false)
const copiedFull = ref(false)

// Color theme gradients
const getThemeGradient = (theme: string): string => {
  const themes: Record<string, string> = {
    purple: 'linear-gradient(135deg,#667eea 0%,#764ba2 100%)',
    blue: 'linear-gradient(135deg,#3b82f6 0%,#1e40af 100%)',
    'light-blue': 'linear-gradient(135deg,#60a5fa 0%,#06b6d4 100%)',
    green: 'linear-gradient(135deg,#10b981 0%,#059669 100%)',
    orange: 'linear-gradient(135deg,#f97316 0%,#ea580c 100%)',
    yellow: 'linear-gradient(135deg,#eab308 0%,#ca8a04 100%)',
    red: 'linear-gradient(135deg,#ef4444 0%,#dc2626 100%)',
    pink: 'linear-gradient(135deg,#ec4899 0%,#db2777 100%)'
  }
  return themes[theme] || themes.purple
}

const embedCode = computed(() => {
  if (!props.agentUrl || !props.agent) return ''
  
  // Get agent names array
  let agentNames: string[] = []
  if (props.agent.agent_names) {
    if (typeof props.agent.agent_names === 'string') {
      try {
        agentNames = JSON.parse(props.agent.agent_names)
      } catch (e) {
        agentNames = []
      }
    } else if (Array.isArray(props.agent.agent_names)) {
      agentNames = props.agent.agent_names
    }
  }
  // Fallback to single name if no names array
  if (agentNames.length === 0) {
    agentNames = [props.agent.name || 'AI Assistent']
  }
  
  const colorTheme = (props.agent as any).color_theme || 'purple'
  const themeGradient = getThemeGradient(colorTheme)
  
  const scriptTag = 'script'
  const agentNamesJson = JSON.stringify(agentNames).replace(/'/g, "\\'")
  
  // Build the base URL without agentName - we'll add it dynamically
  const baseUrl = props.agentUrl
  
  return `<!-- AI Agent Chat Widget - Version 2.0 -->
<div id="ai-chat-widget"></div>
<${scriptTag}>
(function() {
  // Configuration
  var baseUrl = '${baseUrl}';
  var currentDomain = window.location.hostname.replace(/^www\\./, '');
  var themeGradient = '${themeGradient}';
  var agentNames = ${agentNamesJson};
  
  // Get agentId for session storage key
  var agentIdMatch = baseUrl.match(/agentId=([^&]+)/);
  var agentId = agentIdMatch ? agentIdMatch[1] : 'default';
  var sessionKey = 'widget_agent_' + agentId;
  
  // Select agent name: use saved name or pick random
  var savedName = sessionStorage.getItem(sessionKey);
  var agentName;
  
  if (savedName && agentNames.indexOf(savedName) !== -1) {
    // Use saved name if it's still valid
    agentName = savedName;
  } else {
    // Pick random name
    agentName = agentNames[Math.floor(Math.random() * agentNames.length)] || 'AI Assistent';
    sessionStorage.setItem(sessionKey, agentName);
  }
  
  // Build final URL with domain and agentName
  var agentUrl = baseUrl + '&domain=' + encodeURIComponent(currentDomain) + '&agentName=' + encodeURIComponent(agentName);
  
  // Inject styles
  var styles = document.createElement('style');
  styles.textContent = '#ai-chat-button{position:fixed;bottom:20px;right:20px;width:60px;height:60px;border-radius:50%;background:' + themeGradient + ';border:none;cursor:pointer;box-shadow:0 4px 12px rgba(0,0,0,0.15);display:flex;align-items:center;justify-content:center;z-index:9998;transition:transform 0.3s ease}#ai-chat-button:hover{transform:scale(1.1)}#ai-chat-button svg{width:28px;height:28px;color:white}#ai-chat-container{position:fixed;bottom:90px;right:20px;width:380px;max-width:calc(100vw - 40px);height:600px;max-height:calc(100vh - 120px);background:white;border-radius:16px;box-shadow:0 8px 32px rgba(0,0,0,0.12);z-index:9999;display:none;flex-direction:column;overflow:hidden}#ai-chat-container.show{display:flex}#ai-chat-header{background:' + themeGradient + ';color:white;padding:16px 20px;display:flex;justify-content:space-between;align-items:center}#ai-chat-header h3{margin:0;font-size:16px;font-weight:600}#ai-chat-close{background:rgba(255,255,255,0.2);border:none;color:white;width:32px;height:32px;border-radius:50%;cursor:pointer;display:flex;align-items:center;justify-content:center}#ai-chat-iframe{flex:1;border:none;width:100%}';
  document.head.appendChild(styles);
  
  // Create widget HTML
  var container = document.getElementById('ai-chat-widget');
  container.innerHTML = '<button id="ai-chat-button"><svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path></svg></button><div id="ai-chat-container"><div id="ai-chat-header"><h3>' + agentName + '</h3><button id="ai-chat-close"><svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg></button></div><iframe id="ai-chat-iframe" src="' + agentUrl + '"></iframe></div>';
  
  // Event handlers
  document.getElementById('ai-chat-button').onclick = function() {
    document.getElementById('ai-chat-container').classList.toggle('show');
  };
  document.getElementById('ai-chat-close').onclick = function() {
    document.getElementById('ai-chat-container').classList.remove('show');
  };
})();
</${scriptTag}>`
})

const fullPageExample = computed(() => {
  if (!props.agentUrl) return ''
  return `<!DOCTYPE html>
<html lang="sv">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${props.agent?.name || 'AI Assistent'}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background-color: #f9fafb;
    }
    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 20px;
    }
    h1 {
      color: #1f2937;
      margin-bottom: 20px;
      font-size: 24px;
    }
    #ai-agent-widget {
      width: 100%;
      min-height: 600px;
    }
    #ai-agent-widget iframe {
      width: 100%;
      height: 700px;
      border: none;
      border-radius: 12px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    }
  </style>
</head>
<body>
  <div class="container">
    <h1>Chatta med ${props.agent?.name || 'vår AI-assistent'}</h1>
    ${embedCode.value}
  </div>
</body>
</html>`
})

const copyCode = async () => {
  try {
    await navigator.clipboard.writeText(embedCode.value)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch (error) {
    console.error('Failed to copy:', error)
    alert('Kunde inte kopiera. Kopiera manuellt från textfältet.')
  }
}

const copyFullExample = async () => {
  try {
    await navigator.clipboard.writeText(fullPageExample.value)
    copiedFull.value = true
    setTimeout(() => {
      copiedFull.value = false
    }, 2000)
  } catch (error) {
    console.error('Failed to copy:', error)
    alert('Kunde inte kopiera. Kopiera manuellt från textfältet.')
  }
}
</script>


